import "./bulk-tender-carrier-selection-modal.css";

import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { MessageBox } from "@tql/ui-components-react/tql/layout/beta";
import { useContext, useEffect, useState } from "react";

import { useRemoveInsurance } from "~/features/quote/components/carrier-selection/components/add-insurance-modal/add-insurance-hooks.tsx";
import { AddInsuranceModal } from "~/features/quote/components/carrier-selection/components/add-insurance-modal/add-insurance-modal.tsx";
import { CarrierSelectionPanel } from "~/features/quote/components/carrier-selection/components/carrier-selection-panel/carrier-selection-panel.tsx";
import { RateConfirmationModal } from "~/features/quote/components/carrier-selection/components/rate-confirmation-modal/rate-confirmation-modal.tsx";
import { useRoutingGuideToggle } from "~/features/quote/components/carrier-selection/components/shared/hooks/use-routing-guide-toggle.ts";
import { initVal } from "~/features/quote/components/quote-details/constants/quote-detail-constants.ts";
import {
	QuoteDetailsContext,
	QuoteDetailsProvider,
} from "~/features/quote/context/quote-details-context.tsx";
import { useCarrierFilters } from "~/features/quote/hooks/use-carrier-selection-filters-hooks.ts";
import {
	useCarrierSelectionPageLoadQueries,
	useMarginManager,
	useModalManager,
	useSaveAdHocCarrier,
	useSaveSelectedCarrier,
	useUpdateAllCarrierMargins,
} from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import { useGetQuote } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useFeatureFlags } from "~/shared/hooks/use-feature-flags.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import {
	carrierPriceSchema,
	customerCarrierPriceSchema,
} from "~/shared/schemas/carrier-schema.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";

type BulkTenderCarrierSelectionModalProps = {
	quoteId: number;
	onClose: () => void;
	onComplete: () => void;
};

const buildInitialQuoteState = (
	quote: QuoteResponse,
	isInternal: boolean,
): QuoteStateType => {
	const carrierPrices: CarrierPrice[] = isInternal
		? carrierPriceSchema.array().parse(quote.carrierPrices)
		: customerCarrierPriceSchema.array().parse(quote.carrierPrices);

	return {
		...initVal,
		id: quote.id,
		shipmentDate: quote.shipmentDate,
		customer: {
			...initVal.customer,
			id: quote.customer.id,
			name: quote.customer.name,
		},
		customerDetails: {
			...initVal.customerDetails,
			id: quote.customer.id,
			name: quote.customer.name,
		},
		carrierPrices,
		selectedCarrierPriceId: quote.selectedCarrierPriceId,
		eTag: quote.eTag,
		insuranceValue: quote.insuranceValue,
		insuranceApplyTypeId: quote.insuranceApplyTypeId,
		insuranceProductTypeId: quote.insuranceProductTypeId,
	};
};

type BulkTenderCarrierSelectionContentProps = {
	quoteId: number;
	onClose: () => void;
	onComplete: () => void;
};

const BulkTenderCarrierSelectionContent = ({
	quoteId,
	onClose,
	onComplete,
}: BulkTenderCarrierSelectionContentProps) => {
	const { quoteState, addBlankCarrier } = useContext(QuoteDetailsContext);
	const { isInternal } = useDeploymentType();
	const { flags } = useFeatureFlags();
	const { isEnabled: isRoutingGuideEnabled } = useFeatureFlagGate(
		"traxLtlRoutingGuide",
	);
	const { showModal, toggleModal } = useModalManager();
	const {
		marginType,
		marginValue,
		setMarginValue,
		handleMarginTypeChange,
		handleAdjustClick,
		isPending: isAdjustingPrices,
		refreshModal: marginRefreshModal,
	} = useMarginManager();
	const { data: pageData, isLoading: isPageDataLoading } =
		useCarrierSelectionPageLoadQueries(quoteState.customer.id, {
			enabled: quoteState.customer.id > 0,
		});
	const {
		updateAllCarrierMargins,
		isPending: isUpdatingMargins,
		refreshModal: updateMarginsRefreshModal,
	} = useUpdateAllCarrierMargins();
	const {
		handleRemoveInsurance: removeInsurance,
		isPending: isRemovingInsurance,
		refreshModal: removeInsuranceRefreshModal,
	} = useRemoveInsurance();
	const { saveAdHocCarrier } = useSaveAdHocCarrier();
	const { isPending, mutateAsync: saveSelectedCarrier } =
		useSaveSelectedCarrier();
	const {
		updateFilter,
		setCurrentTab,
		sortDirection,
		setSortDirection,
		getFilteredAndSortedCarriers,
	} = useCarrierFilters();
	const {
		isGuideActive,
		showingHidden,
		trigger,
		filteredCarriers,
		hiddenCarrierCount,
		handleToggleGuide,
		handleToggleHiddenCarriers,
	} = useRoutingGuideToggle(quoteState.carrierPrices);
	const displayedCarriers = getFilteredAndSortedCarriers(filteredCarriers);
	const showRoutingGuideBanner = isRoutingGuideEnabled && isInternal;
	const [selectedCarrierPriceId, setSelectedCarrierPriceId] = useState<
		number | null
	>(quoteState.selectedCarrierPriceId);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const isUpdating = isSaving || isPending;
	useAutoSpinner(
		isUpdating ||
			isRemovingInsurance ||
			isAdjustingPrices ||
			isUpdatingMargins ||
			isPageDataLoading,
	);

	useEffect(() => {
		setSelectedCarrierPriceId(quoteState.selectedCarrierPriceId);
		setErrorMessage(null);
	}, [quoteId, quoteState.selectedCarrierPriceId]);

	const handleSelectCarrier = async (carrier: CarrierPrice): Promise<void> => {
		setErrorMessage(null);
		if (carrier.isAdHoc && carrier.id < 0) {
			setIsSaving(true);
			try {
				const result = await saveAdHocCarrier(
					carrier,
					quoteState.id,
					quoteState.eTag,
				);
				if (!result.success) {
					setErrorMessage(result.error);
					return;
				}
				setSelectedCarrierPriceId(result.data.carrierPrice.id);
			} catch {
				setErrorMessage("Unable to add the carrier. Please try again.");
			} finally {
				setIsSaving(false);
			}
			return;
		}
		setSelectedCarrierPriceId(carrier.id);
	};

	const handleRemoveInsuranceClick = async (): Promise<void> => {
		let eTag = quoteState.eTag;
		if (isInternal) {
			const updatedETag = await updateAllCarrierMargins(
				quoteState.id,
				quoteState.eTag,
				quoteState.carrierPrices,
			);
			if (!updatedETag) return;
			eTag = updatedETag;
		}
		await removeInsurance({ eTag, id: quoteState.id });
	};

	const handleConfirm = async (): Promise<void> => {
		if (selectedCarrierPriceId == null) return;

		setErrorMessage(null);
		setIsSaving(true);
		try {
			const result = await saveSelectedCarrier({
				id: quoteState.id,
				eTag: quoteState.eTag,
				selectedCarrierPriceId,
				insuranceApplyTypeId: quoteState.insuranceApplyTypeId,
				insuranceProductTypeId: quoteState.insuranceProductTypeId,
				insuranceValue: quoteState.insuranceValue,
			});
			if (!result.success) {
				setErrorMessage(result.error);
				return;
			}
			onComplete();
		} catch {
			setErrorMessage("Unable to update the carrier. Please try again.");
		} finally {
			setIsSaving(false);
		}
	};

	const isAddInsuranceEnabled = flags["traxLtlCsAddInsurance"] === true;
	const isSaveQuoteEnabled =
		flags["traxLtlCsSaveButton"] === true && isInternal;

	return (
		<>
			{marginRefreshModal}
			{updateMarginsRefreshModal}
			{removeInsuranceRefreshModal}
			<AddInsuranceModal
				defaultProductType={quoteState.insuranceProductTypeId ?? undefined}
				defaultShipmentValue={quoteState.insuranceValue}
				onToggleModal={toggleModal}
				quoteId={quoteState.id}
				showModal={showModal.addInsurance}
			/>
			<RateConfirmationModal
				carriers={displayedCarriers}
				onToggleModal={toggleModal}
				showModal={showModal.confirmationRate}
			/>
			<TraxModal
				cancelText={"Cancel"}
				className={"bulk-tender-carrier-selection-modal"}
				confirmDisabled={
					selectedCarrierPriceId == null ||
					selectedCarrierPriceId === quoteState.selectedCarrierPriceId ||
					isUpdating
				}
				confirmText={isUpdating ? "Updating..." : "Update Selection"}
				onCancel={onClose}
				onClose={onClose}
				onConfirm={() => void handleConfirm()}
				showConfirmationButtons={true}
				title={"Select a Carrier"}
				width={"98%"}
			>
				{errorMessage ? (
					<MessageBox status={"error"} title={"Unable to Update Carrier"}>
						{errorMessage}
					</MessageBox>
				) : null}
				<CarrierSelectionPanel
					addBlankCarrier={addBlankCarrier}
					carrierPrices={quoteState.carrierPrices}
					displayedCarriers={displayedCarriers}
					hasRoutingGuides={quoteState.carrierPrices.some(
						(carrier) => carrier.routingGuideApplied != null,
					)}
					hiddenCarrierCount={hiddenCarrierCount}
					isAddInsuranceEnabled={isAddInsuranceEnabled}
					isAdjustingPrices={isAdjustingPrices}
					isBookingDisabled={(carrier) =>
						Boolean(
							isGuideActive &&
							carrier.routingGuideApplied !== true &&
							!carrier.isAdHoc,
						)
					}
					isGuideActive={isGuideActive}
					isRemovingInsurance={isRemovingInsurance}
					isSaveQuoteEnabled={isSaveQuoteEnabled}
					isUpdatingMargins={isUpdatingMargins}
					marginType={marginType}
					marginValue={marginValue ?? 0}
					onAdjustClick={handleAdjustClick}
					onFilterChange={updateFilter}
					onMarginTypeChange={handleMarginTypeChange}
					onRemoveInsurance={() => void handleRemoveInsuranceClick()}
					onSelectCarrier={(carrier) => void handleSelectCarrier(carrier)}
					onSetMarginValue={setMarginValue}
					onSortDirectionChange={setSortDirection}
					onStepChange={() => undefined}
					onTabSelect={setCurrentTab}
					onToggleGuide={handleToggleGuide}
					onToggleHiddenCarriers={handleToggleHiddenCarriers}
					onToggleModal={toggleModal}
					onUpdateAllCarrierMargins={(id, eTag, carriers) => {
						void updateAllCarrierMargins(id, eTag, carriers);
					}}
					pageData={pageData}
					selectedCarrierPriceId={selectedCarrierPriceId}
					showRoutingGuideBanner={showRoutingGuideBanner}
					showingHidden={showingHidden}
					sortDirection={sortDirection}
					trigger={trigger}
					wrapperClassName={"bulk-tender-carrier-selection"}
				/>
			</TraxModal>
		</>
	);
};

export const BulkTenderCarrierSelectionModal = ({
	quoteId,
	onClose,
	onComplete,
}: BulkTenderCarrierSelectionModalProps) => {
	const { isInternal } = useDeploymentType();
	const {
		data: fetchedQuote,
		isError: isQuoteLoadError,
		isLoading: isLoadingQuote,
	} = useGetQuote(quoteId);
	const quote = fetchedQuote;

	if (isLoadingQuote) {
		return (
			<TraxModal
				cancelText={"Cancel"}
				className={"bulk-tender-carrier-selection-modal"}
				onCancel={onClose}
				onClose={onClose}
				showConfirmButton={false}
				showConfirmationButtons={true}
				title={"Select a Carrier"}
				width={"98%"}
			>
				<div
					aria-label={"Loading available carriers"}
					className={"bulk-tender-carrier-selection-loading"}
					role={"status"}
				>
					<Loader size={"medium"} type={"infinite-spinner"} />
					Loading available carriers...
				</div>
			</TraxModal>
		);
	}

	if (!quote) {
		return (
			<TraxModal
				cancelText={"Close"}
				className={"bulk-tender-carrier-selection-modal"}
				onCancel={onClose}
				onClose={onClose}
				showConfirmButton={false}
				showConfirmationButtons={true}
				title={"Select a Carrier"}
				type={"error"}
				width={"98%"}
			>
				<MessageBox status={"error"} title={"Carrier Selection Unavailable"}>
					{isQuoteLoadError
						? "The quote could not be loaded. Refresh the board and retry."
						: "The quote could not be found. Refresh the board and retry."}
				</MessageBox>
			</TraxModal>
		);
	}

	return (
		<QuoteDetailsProvider
			initialQuoteState={buildInitialQuoteState(quote, isInternal)}
		>
			<BulkTenderCarrierSelectionContent
				onClose={onClose}
				onComplete={onComplete}
				quoteId={quoteId}
			/>
		</QuoteDetailsProvider>
	);
};
